import './styles.scss';

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import axios from 'axios';
import Hashids from 'hashids';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import Autocomplete from 'react-google-autocomplete';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { toast as Toast } from 'react-toastify';
import {
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
  Card,
  Input,
  InputGroup,
  InputGroupText,
  Modal,
  UncontrolledAccordion,
} from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';

import Breadcrumb from '@/components/common/breadcrumb.component';
import MediaCropper from '@/components/cropper';
import Dropzone from '@/components/dropzone';
import Icon from '@/components/icon';
import ImageImportModal from '@/components/image-import-modal';
import ModalDialog from '@/components/modal-dialog';
import ToastContent from '@/components/toasts/content';
import { Constants } from '@/constants';
import { postVenue, putVenue } from '@/redux/actions/venue.actions';
import { uploadToS3 } from '@/utils/imageUtils';

const env = process.env.NODE_ENV || 'development';
const config = require('../../../config/config.json')[env];

const hashids = new Hashids('', 12);

const defaultAmenities = [
  'Accepts Credit Cards',
  'Accepts Cryptocurrency',
  'Free WiFi',
  'Parking - Private Lot',
  'Parking - On Street',
  'Parking - Valet',
  'Walk-In Humidor',
  'Lounge',
  'Alcohol - All',
  'Alcohol - Beer & Wine Only',
  'Alcohol - Beer Only',
  'Kitchen',
  'Outdoor Seating',
  'Military Discounts',
  'Wheelchair Accessible',
  'Pool Tables',
  'TV',
];

const days = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const hourTimeValues = () => {
  const x = 5; // minutes interval
  const times = [];
  let tt = 0;
  const ap = ['AM', 'PM'];
  for (let i = 0; tt < 24 * 60; i++) {
    const hh = Math.floor(tt / 60);
    let h = hh % 12;
    if (h < 1) {
      h = 12;
    }
    const mm = tt % 60;
    const time = `${h}:${`0${mm}`.slice(-2)} ${ap[Math.floor(hh / 12)]}`;
    times[i] = {
      value: time,
      label: time,
    };
    tt += x;
  }
  return times;
};

const hourTimeDefaults = hourTimeValues();

const VenueEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const [state, setState] = React.useState({
    active: false,
    selectedName: '',
    selectedDescription: '',
    selectedAddress: null,
    selectedWebsite: '',
    selectedPhone: '',
    selectedEmail: '',
    selectedYelpId: '',
    selectedFoursquareId: '',
    selectedGoogleId: '',
    selectedTwitterId: '',
    selectedFacebookId: '',
    selectedInstagramId: '',
    virtualTourEmbedCode: '',
    virtualTourEmbedIframe: '',
    selectedVenueImages: [],
    selectedImagesToAttach: [],
    selectedImagesToRemove: [],
    venueProfileImage: null,
    venueCoverImage: null,
    selectedHoursType: 'custom',
    selectedMonday: false,
    selectedTuesday: false,
    selectedWednesday: false,
    selectedThursday: false,
    selectedFriday: false,
    selectedSaturday: false,
    selectedSunday: false,
    selectedHours: null,
    selectedTags: [],
    selectedCanBuy: false,
    selectedCanBuyOnline: false,
    selectedCanSmoke: false,
    selectedEvents: null,
  });

  const [hasId, setHasId] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [isPending, setIsPending] = useState(true);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const [showImageUrlModal, setShowImageUrlModal] = useState(false);

  const [showHoursModal, setShowHoursModal] = useState(false);

  const [selectedImageHolder, setSelectedImageHolder] = useState<string | null>(
    null
  );
  const [selectedImageFile, setSelectedImageFile] = useState<string | null>(
    null
  );
  const [showMediaCropper, setShowMediaCropper] = useState(false);
  const [generating, setGenerating] = useState<string | false>(false);

  const venueCoverImage = useRef(null);
  const venueProfileImage = useRef(null);
  const venueImages = useRef(null);

  const updateState = (newState: any) =>
    setState((prev: any) => ({ ...prev, ...newState }));

  const handleInputChange = (field) => (e) => {
    setState((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleCheckboxChange = (field) => (e) => {
    setState((prev) => ({ ...prev, [field]: e.target.checked }));
  };

  const handleSelectChange = (field) => (value) => {
    setState((prev) => ({ ...prev, [field]: value }));
  };

  const addressFromComponents = (components) => {
    const address = {};
    components.forEach((comp) => {
      if (comp.types.includes('street_number')) {
        address.street_number = comp.long_name;
      }
      if (comp.types.includes('route')) {
        address.street =
          `${address.street_number || ''} ${comp.long_name}`.trim();
      }
      if (comp.types.includes('locality')) {
        address.city = comp.long_name;
      }
      if (comp.types.includes('administrative_area_level_1')) {
        address.state = comp.short_name;
      }
      if (comp.types.includes('postal_code')) {
        address.zip = comp.long_name;
      }
      if (comp.types.includes('country')) {
        address.country = comp.long_name;
      }
    });
    return address;
  };

  const handleAddressChange = (key) => (event) => {
    const address = state.selectedAddress;
    address[key] = event.target.value;
    updateState({
      selectedAddress: address,
    });
  };

  const addressComponents = () => {
    const address = state.selectedAddress || {};
    return (
      <div>
        <div>
          <hr />
          <div className="row">
            <div className="col-md-12">
              Country
              <input
                className="form-control"
                type="text"
                name="state"
                value={address.country}
                style={{ textTransform: 'capitalize', marginBottom: 10 }}
                onChange={handleAddressChange('country')}
              />
            </div>
            <div className="col-md-12">
              Street
              <input
                className="form-control"
                type="text"
                name="street"
                value={address.street}
                style={{ textTransform: 'capitalize', marginBottom: 10 }}
                onChange={handleAddressChange('street')}
              />
            </div>
            <div className="col-md-4">
              City / Town
              <input
                className="form-control"
                type="text"
                name="city"
                value={address.city}
                style={{ textTransform: 'capitalize' }}
                onChange={handleAddressChange('city')}
              />
            </div>
            <div className="col-md-4">
              State / Province
              <input
                className="form-control"
                type="text"
                name="state"
                value={address.state}
                style={{ textTransform: 'capitalize' }}
                onChange={handleAddressChange('state')}
              />
            </div>
            <div className="col-md-4">
              Zip / Postal Code
              <input
                className="form-control"
                type="text"
                name="zip"
                value={address.zip}
                style={{ textTransform: 'capitalize' }}
                onChange={handleAddressChange('zip')}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const periodAtIndex = (index) => {
    for (let i = 0; i < state.selectedHours.length; i++) {
      const period = state.selectedHours[i];
      if (period.open && period.open.day === index) {
        return period;
      }
    }
    return null;
  };

  const formatHoursDisplay = (day, index) => {
    const period = periodAtIndex(index);
    if (period) {
      console.log('Found period!');
      console.log(period);
    }
    let openValue = 'Unknown';
    let closedValue = 'Unknown';
    if (period && (period.open || period.close)) {
      if (period.open) {
        let openHours = period.open.hours;
        const openSuffix = openHours >= 12 ? 'PM' : 'AM';
        openHours = openHours % 12 || 12;
        const openMinutes =
          (period.open.minutes < 10 ? '0' : '') + period.open.minutes;
        openValue = `${openHours}:${openMinutes.slice(-2)} ${openSuffix}`;
      }

      if (period.close) {
        let closedHours = period.close.hours;
        const closedSuffix = closedHours >= 12 ? 'PM' : 'AM';
        closedHours = closedHours % 12 || 12;
        const closedMinutes =
          (period.close.minutes < 10 ? '0' : '') + period.close.minutes;
        closedValue = `${closedHours}:${closedMinutes.slice(-2)} ${closedSuffix}`;
      }
      return (
        <div>
          {day}: {`${openValue} - ${closedValue}`}
        </div>
      );
    }
    if (!state.selectedHours || state.selectedHours.length === 0) {
      return <div>{day}: Unknown</div>;
    }
    return <div>{day}: Closed</div>;
  };

  const renderBusinessHours = () => {
    const { selectedHours } = state;
    console.log('Rendering business hours:');
    console.log(selectedHours);
    if (selectedHours && selectedHours.length === 1) {
      if (selectedHours[0].permanently_closed) {
        return <div>Permanently Closed</div>;
      }
      if (selectedHours[0].always_open) {
        return <div>Always Open</div>;
      }
    }
    return (
      <div>
        {days.map((day, i) => (
          <div>{formatHoursDisplay(day, i)}</div>
        ))}
      </div>
    );
  };

  // TODO Show table with upcoming events - if more than 5, include "Show All" button and navigate to "Events" page with filter
  const renderEvents = () => <div>Events UI (coming soon)</div>;

  const venueImageUrl = (image) => image.url || '';

  useEffect(() => {
    // TODO: Anything?
  }, []);

  useEffect(() => {
    if (
      !location.state?.venue ||
      (params.id &&
        params.id !== location.state?.venue.hash_id &&
        params.id !== 'new')
    ) {
      // getVenueById(hashids.decode(params.id));
    }
    setHasId(params.id !== 'new');
  }, [params.id]);

  const populateForm = (venue) => {
    if (venue) {
      // console.log(JSON.stringify(venue));
      setIsPending(!venue.active);

      let address = null;

      if (venue.location) {
        const { location: venueLocation } = venue;
        address = {
          street: venueLocation.address1 || null,
          city: venueLocation.city || null,
          state: venueLocation.state || null,
          zip: venueLocation.zip || null,
          country: venueLocation.country || null,
          latitude:
            venueLocation.coordinate !== null
              ? venueLocation.coordinate.latitude
              : null,
          longitude:
            venueLocation.coordinate !== null
              ? venueLocation.coordinate.longitude
              : null,
        };
      }

      updateState({
        selectedId: venue.id ? venue.id : null,
        selectedVenueImages: venue.images,
        selectedName: venue.name || '',
        selectedDescription: venue.description || '',
        selectedCheckinSummary: venue.checkin_summary || '',
        selectedFaqs: venue.faqs ? JSON.parse(venue.faqs) : [],
        selectedWebsite: venue.website || '',
        selectedAddress: address,
        selectedEmail: venue.email || '',
        selectedPhone: venue.phone || '',
        selectedYelpId: venue.yelp_id || '',
        selectedFoursquareId: venue.foursquare_id || '',
        selectedGoogleId: venue.place_id || venue.google_id || '',
        selectedTwitterId: venue.twitter_id || '',
        selectedFacebookId: venue.facebook_id || '',
        selectedInstagramId: venue.instagram_id || '',
        selectedCanBuy: venue.can_buy || '',
        selectedCanBuyOnline: venue.can_buy_online || '',
        selectedCanSmoke: venue.can_smoke || '',
        selectedHours: venue.hours.map((hour) => {
          // FIXME If either of these are present, we really don't need any of the others - hopefully they can either be removed from the table or filtered out server-side
          if (hour.permanently_closed || hour.always_open) {
            return hour;
          }
          return {
            open: {
              day: hour.open_day,
              time:
                hour.open_time && hour.open_time.toString().padStart(4, '0'),
              hours:
                hour.open_time &&
                hour.open_time.toString().padStart(4, '0').substring(0, 2),
              minutes:
                hour.open_time &&
                hour.open_time.toString().padStart(4, '0').substring(2, 4),
            },
            close: {
              day: hour.close_day,
              time:
                hour.close_time && hour.close_time.toString().padStart(4, '0'),
              hours:
                hour.close_time &&
                hour.close_time.toString().padStart(4, '0').substring(0, 2),
              minutes:
                hour.close_time &&
                hour.close_time.toString().padStart(4, '0').substring(2, 4),
            },
          };
        }),
        selectedTags: venue.tags.map((venueTag) => ({
          value: venueTag.tag.title,
          label: venueTag.tag.title,
        })),
        virtualTourEmbedIframe: venue.virtual_tour_embed, // FIXME These should probably be an array at some point
        active: venue.active,
        venueProfileImage:
          venue.icon_url || venue.profile_image_url || undefined,
        venueCoverImage: venue.cover_url || venue.cover_image_url || undefined,
      });
    }
  };

  const updateVenueMedia = (productId) => {
    const {
      selectedImagesToAttach, // FIXME Technically can be a video down the road, too
      selectedImagesToRemove,
    } = state;

    if (
      selectedImagesToAttach.length > 0 ||
      selectedImagesToRemove.length > 0
    ) {
      // FIXME Check to see what we need to pass - I think we need to know if it's from Google or a similar site vs user uploaded
      axios
        .put(`${Constants.apiPath}/venues/${productId}/media`, {
          add: selectedImagesToAttach.map((url) => ({
            url,
            external_id_type: 'google', // TODO Would like a better way to determine this - right now, they will likely all come from Google
          })),
          remove: selectedImagesToRemove,
        })
        .then(() => {
          console.log('Added media successfully!');
          // TODO Do anything? Update the local state?
        })
        .catch((err) => {
          console.log('Unable to add media');
          console.log(err);
        });
    }
  };

  const saveVenue = () => {
    console.log('Submitting!');
    console.log(state);

    const { selectedTags, selectedHoursType, selectedHours } = state;

    const tags = [];
    if (selectedTags.length) {
      for (let i = 0; i < selectedTags.length; i++) {
        tags.push(selectedTags[i].value);
      }
    }

    let hours = null;
    // let { active } = state;
    if (selectedHoursType) {
      hours = [];

      if (selectedHoursType === 'custom') {
        if (selectedHours) {
          console.log('Selected hours:');
          console.log(selectedHours);
          for (const hour of selectedHours) {
            if (hour.open && hour.close) {
              console.log('Adding time slot:');
              console.log(hour);
              // FIXME If only open exists with 0000, what does that mean? Always open? Hours unknown? Throw it out?
              hours.push({
                open_day: hour.open.day,
                open_time: hour.open.time && hour.open.time.padStart(4, '0'),
                close_day: hour.close.day,
                close_time: hour.close.time && hour.close.time.padStart(4, '0'),
              });
            }
          }
          // active = true;
        }
      } else if (selectedHoursType === 'always_open') {
        hours.push({ always_open: true, permanently_closed: false });
        // active = true;
      } else if (selectedHoursType === 'closed') {
        hours.push({ permanently_closed: true, always_open: false });
        // active = false;
      }
    }

    console.log('Hours:');
    console.log(hours);

    const venue = {
      name: state.selectedName,
      description: state.selectedDescription,
      checkin_summary: state.selectedCheckinSummary,
      faqs:
        state.selectedFaqs?.length > 0
          ? JSON.stringify(state.selectedFaqs)
          : null,
      website: state.selectedWebsite,
      phone: state.selectedPhone,
      email: state.selectedEmail,
      addr_line_1: state.selectedAddress ? state.selectedAddress.street : null,
      addr_line_2: null, // TODO
      addr_city: state.selectedAddress ? state.selectedAddress.city : null,
      addr_state: state.selectedAddress ? state.selectedAddress.state : null,
      addr_zip: state.selectedAddress ? state.selectedAddress.zip : null,
      addr_country: state.selectedAddress
        ? state.selectedAddress.country
        : null,
      latitude: state.selectedAddress ? state.selectedAddress.latitude : null,
      longitude: state.selectedAddress ? state.selectedAddress.longitude : null,
      yelp_id: state.selectedYelpId,
      foursquare_id: state.selectedFoursquareId,
      google_id: state.selectedGoogleId,
      place_id: state.selectedGoogleId, // FIXME This one is deprecated, but include anyway for now
      twitter_id: state.selectedTwitterId,
      facebook_id: state.selectedFacebookId,
      instagram_id: state.selectedInstagramId,
      can_buy: state.selectedCanBuy === true,
      can_buy_online: state.selectedCanBuyOnline === true,
      can_smoke: state.selectedCanSmoke === true,
      virtual_tour_embed: state.virtualTourEmbedCode || null,
      verified: true, // FIXME What defines this? Edited?
      custom: false, // TODO Pull from a toggle?
      active: state.active === true,
      // FIXME Decide on which to use of these 4
      icon_url: state.venueProfileImage || undefined,
      profile_image_url: state.venueProfileImage || undefined,
      cover_url: state.venueCoverImage || undefined,
      cover_image_url: state.venueCoverImage || undefined,
      hours,
      tags,
    };

    // Prevent empty strings
    Object.keys(venue).forEach(function (key, index) {
      if (this[key] === '') {
        this[key] = null;
      }
    }, venue);

    if (params.id && params.id !== 'new') {
      venue.id = hashids.decode(params.id)[0];
      putVenue(venue)
        .then((updatedVenue) => {
          Toast.success(
            <ToastContent
              message="Venue updated successfully"
              title={updatedVenue.name}
            />,
            {
              position: 'top-right',
            }
          );
        })
        .catch((err) => {
          console.log(err);
          console.log('Showing error alert...');
          ModalDialog.show({
            title: 'Error',
            type: 'error',
            message:
              'Unable to add the venue details. Please contact an admin.',
            buttons: [
              {
                label: 'Dismiss',
                onClick: () => {
                  ModalDialog.close();
                },
              },
            ],
          });
        });

      updateVenueMedia(venue.id);
    } else {
      postVenue(venue)
        .then((updatedVenue) => {
          Toast.success(
            <ToastContent
              message="Venue added successfully"
              title={updatedVenue.name}
            />,
            {
              position: 'top-right',
            }
          );

          updateVenueMedia(updatedVenue.id);
        })
        .catch((err) => {
          console.log(err);
          console.log('Showing error alert...');
          ModalDialog.show({
            title: 'Error',
            type: 'error',
            message:
              'Unable to add the venue details. Please contact an admin.',
            buttons: [
              {
                label: 'Dismiss',
                onClick: () => {
                  ModalDialog.close();
                },
              },
            ],
          });
        });
    }

    console.log(venue);
  };

  const deleteVenue = () => {
    ModalDialog.show({
      type: 'warning',
      title: 'Delete Venue?',
      message:
        'This will delete the venue and all related info and cannot be undone. Note: If users have any checkins associated with this venue, this will return an error. If that happens, you will need to merge the venue instead.',
      buttons: [
        {
          label: 'No, Cancel',
          onClick: () => {
            ModalDialog.close();
          },
        },
        {
          label: 'Yes, Delete',
          color: 'danger',
          onClick: () => {
            // deleteVenue();
          },
        },
      ],
    });
  };

  useEffect(() => {
    if (location.state?.venue) {
      // If venue details were passed via the router location state, update state as needed.
      const { venue } = location.state;
      populateForm(venue);
    }
  }, [location.state?.venue]);

  const generateDescription = () => {
    if (params.id !== 'new') {
      setGenerating('description');
      const venueId = hashids.decode(params.id)[0];

      axios
        .get(`${Constants.aiPath}/admin/venues/${venueId}/description`, {})
        .then((response) => {
          updateState({ selectedDescription: response.data.description });
        })
        .catch((err) => {
          Toast.error(
            <ToastContent
              message="Unable to generate venue description. Please contact an admin."
              title="Generation Unsuccessful"
            />,
            {
              position: 'top-right',
            }
          );
          console.error(err);
        })
        .finally(() => setGenerating(false));
    }
  };

  const generateCheckinSummary = () => {
    if (params.id !== 'new') {
      setGenerating('checkin_summary');
      const venueId = hashids.decode(params.id)[0];

      axios
        .get(`${Constants.aiPath}/admin/venues/${venueId}/checkins/summary`, {})
        .then((response) => {
          if (!response.data.summary && response.data.message) {
            Toast.warning(
              <ToastContent
                message={response.data.message}
                title="Generation Unsuccessful"
              />,
              {
                position: 'top-right',
              }
            );
          }
          updateState({ selectedCheckinSummary: response.data.summary });
        })
        .catch((err) => {
          Toast.error(
            <ToastContent
              message="Unable to generate venue checkin summary. Please contact an admin."
              title="Generation Unsuccessful"
            />,
            {
              position: 'top-right',
            }
          );
          console.error(err);
        })
        .finally(() => setGenerating(false));
    }
  };

  const generateFaqs = () => {
    if (params.id !== 'new') {
      setGenerating('faqs');
      const venueId = hashids.decode(params.id)[0];

      axios
        .get(`${Constants.aiPath}/admin/venues/${venueId}/faqs`, {})
        .then((response) => {
          if (!response.data.faqs && response.data.message) {
            Toast.warning(
              <ToastContent
                message={response.data.message}
                title="Generation Unsuccessful"
              />,
              {
                position: 'top-right',
              }
            );
          }
          updateState({ selectedFaqs: response.data.faqs });
        })
        .catch((err) => {
          Toast.error(
            <ToastContent
              message="Unable to generate venue FAQs. Please contact an admin."
              title="Generation Unsuccessful"
            />,
            {
              position: 'top-right',
            }
          );
          console.error(err);
        })
        .finally(() => setGenerating(false));
    }
  };

  const getBadgeTitle = useCallback(() => {
    if (hasId) {
      return isPending ? 'Inactive' : 'Active';
    }
    if (hasData) {
      return 'Draft';
    }
    return null;
  }, [hasId, hasData, isPending]);

  const getBadgeColor = useCallback(() => {
    if (hasId && !isPending) {
      return 'success';
    }
    return 'secondary';
  }, [hasId, hasData, isPending]);

  const onFileDrop = (imageHolder) => (file) => {
    console.log('Received file for holder:', imageHolder);
    if (file) {
      console.log(file);
      console.log('Loading file...');
      const fileReader = new FileReader();
      fileReader.onload = (event) => {
        console.log('Loaded file.');
        const dataUrl = event.target.result;
        console.log(dataUrl);
        setSelectedImageHolder(imageHolder);
        setSelectedImageFile(dataUrl);
        setShowMediaCropper(true);
      };
      fileReader.readAsDataURL(file);
    }
  };

  const handleRadioChange = (key) => (event) => {
    updateState({ [key]: event.target.value });
  };

  const onHoursFocusChange = (index) => () => {
    switch (index) {
      case 0: {
        // INFO Starts on Sunday from Google
        updateState({
          selectedSunday: true,
        });
        break;
      }
      case 1: {
        updateState({
          selectedMonday: true,
        });
        break;
      }
      case 2: {
        updateState({
          selectedTuesday: true,
        });
        break;
      }
      case 3: {
        updateState({
          selectedWednesday: true,
        });
        break;
      }
      case 4: {
        updateState({
          selectedThursday: true,
        });
        break;
      }
      case 5: {
        updateState({
          selectedFriday: true,
        });
        break;
      }
      case 6: {
        updateState({
          selectedSaturday: true,
        });
        break;
      }
      default:
        break;
    }
  };

  const handleHoursChange = (index, type) => (selection) => {
    console.log('Hours selection:');
    console.log(selection);
    const hours = state.selectedHours;

    let time = selection.value; // 9:15 PM
    console.log(time);

    const pm = time.indexOf('PM') !== -1;

    const parts = time.replace(' AM', '').replace(' PM', '').split(':');
    let hour = parts[0];
    const min = parts[1];

    time = parseInt(
      time.replace(':', '').replace(' AM', '').replace(' PM', '').trim()
    );

    if (pm && hour < 12) {
      hour = (parseInt(hour) + 12).toString();
      time += 1200;
    }
    // eslint-disable-next-line eqeqeq
    if (!pm && hour == 12) {
      hour = (parseInt(hour) - 12).toString();
      time -= 1200;
    }

    if (!hours[index]) {
      hours[index] = {};
    }

    hours[index][type] = {
      day: index,
      time,
      hours: hour,
      minutes: min,
    };

    console.log('Selected hours:');
    console.log(hours);

    updateState({
      selectedHours: hours,
    });
  };

  const setCheckedForIndex = (index, checked) => {
    return () => {
      // FIXME Not working, get infinite loop when this return is missing, no change when it's here
      switch (index) {
        case 0: {
          // INFO Starts on Sunday from Google
          updateState({
            selectedSunday: checked,
          });
          break;
        }
        case 1: {
          updateState({
            selectedMonday: checked,
          });
          break;
        }
        case 2: {
          updateState({
            selectedTuesday: checked,
          });
          break;
        }
        case 3: {
          updateState({
            selectedWednesday: checked,
          });
          break;
        }
        case 4: {
          updateState({
            selectedThursday: checked,
          });
          break;
        }
        case 5: {
          updateState({
            selectedFriday: checked,
          });
          break;
        }
        case 6: {
          updateState({
            selectedSaturday: checked,
          });
          break;
        }
        default:
          break;
      }
    };
  };

  const formatHoursTime = (index, type) => {
    const { selectedHours } = state;
    if (selectedHours) {
      const time = selectedHours[index];
      console.log(time);
      if (time) {
        const data = time[type];
        // console.log('Formatting hours data:');
        // console.log(data);
        if (data) {
          // TODO Can use data.day - should we make sure it matches the index?
          setCheckedForIndex(index, true);
          let { hours } = data;
          const suffix = hours >= 12 ? 'PM' : 'AM';
          hours = hours % 12 || 12;
          const minutes = (data.minutes < 10 ? '0' : '') + data.minutes;
          const value = `${hours}:${minutes.slice(-2)} ${suffix}`;
          return {
            value,
            label: value,
          };
        }
      }
    }
    // FIXME Will this cause an issue when the text is empty?
    // setCheckedForIndex(index, false);
    return ''; // FIXME Default
  };

  const uploadMedia = (blob, extension, activeDropzoneId) => {
    setUploadingMedia(true);
    const uuid = uuidv4();
    const venueHash =
      params.id && params.id !== 'new' ? params.id : new Date().getTime();
    const filename = `${venueHash}-${activeDropzoneId}.${extension}`;
    uploadToS3(blob, `venues/${activeDropzoneId}`, filename, uuid)
      .then((res) => {
        console.log('Uploaded to media server!');
        console.log(res.data);
        updateState({
          uploadingMedia: false,
          [activeDropzoneId]: res.data.media_url,
        });
      })
      .catch((err) => {
        setUploadingMedia(true);
        // ErrorLogger.captureException(err);
        console.error(err);
      });
  };

  const renderImageFromUrlModal = () => (
    <ImageImportModal
      type="venues"
      open={showImageUrlModal}
      onClose={() => setShowImageUrlModal(false)}
      onResult={(media) => {
        console.log('Image imported successfully');
        console.log(media);
        const images = [...state.selectedVenueImages];
        const imagesToAdd = [...state.selectedImagesToAttach];
        images.push({
          id: media.id,
          url: media.media_url,
        });
        imagesToAdd.push(media.media_url);
        updateState({
          selectedVenueImages: images,
          selectedImagesToAttach: imagesToAdd,
        });
        setShowImageUrlModal(false);
      }}
    />
  );

  const imageAspectRatios = {
    venueProfileImage: 1,
    venueCoverImage: 1000 / 300,
  };

  const renderMediaCropper = () => {
    const activeDropzoneId = selectedImageHolder;
    return (
      <MediaCropper
        aspectRatio={imageAspectRatios[activeDropzoneId] || undefined}
        src={selectedImageFile}
        open={showMediaCropper}
        toggle={() => setShowMediaCropper(false)}
        onClose={() => setShowMediaCropper(false)}
        onSave={(croppedCanvas) => {
          console.log('Saving cropped image...');
          croppedCanvas.toBlob((blob) => {
            console.log('Canvas blob');
            console.log(blob);
            console.log(activeDropzoneId);
            const fileReader = new FileReader();
            fileReader.onload = (event) => {
              const dataUrl = event.target.result;
              console.log(dataUrl);
              updateState({
                [activeDropzoneId]: dataUrl,
              });
            };
            fileReader.readAsDataURL(blob);

            const base64 = croppedCanvas.toDataURL('image/jpeg');
            uploadMedia(
              blob,
              base64.split(';')[0].split('/')[1],
              activeDropzoneId
            );

            setSelectedImageHolder(null);
            setSelectedImageFile(null);
            setShowMediaCropper(false);
          });
        }}
      />
    );
  };

  const renderHoursModal = () => (
    <Modal
      isOpen={showHoursModal}
      onClosed={() => setShowHoursModal(false)}
      toggle={() => setShowHoursModal(false)}
    >
      <div className="modal-header">
        <h5 className="modal-title">Business Hours</h5>
      </div>
      <div className="modal-body md">
        <div className="row">
          <div className="col-md-12" style={{ paddingLeft: 20 }}>
            <FormControl>
              <RadioGroup
                aria-labelledby="hours-type"
                defaultValue="custom"
                name="hours-type"
                value={state.selectedHoursType}
                onChange={handleRadioChange('selectedHoursType')}
              >
                <FormControlLabel
                  value="custom"
                  control={<Radio />}
                  label="Open on selected hours"
                />
                <FormControlLabel
                  value="always_open"
                  control={<Radio />}
                  label="Always open"
                />
                <FormControlLabel
                  value="no_hours"
                  control={<Radio />}
                  label="No hours available"
                />
                <FormControlLabel
                  value="closed"
                  control={<Radio />}
                  label="Closed permanently"
                />
              </RadioGroup>
            </FormControl>
          </div>
        </div>

        <div
          style={
            state.selectedHoursType === 'custom'
              ? { marginTop: 10 }
              : { display: 'none' }
          }
        >
          {/* FIXME Make these re-usable components? */}
          <Card style={{ padding: 10 }}>
            <div className="row" style={{ marginBottom: 6 }}>
              <div className="col-md-4">
                <div>
                  <FormControlLabel
                    id="sunday-cb"
                    name="days"
                    control={<Checkbox checked={state.selectedSunday} />}
                    onChange={handleCheckboxChange('selectedSunday')}
                    label="Sunday"
                  />
                </div>
              </div>

              <div className="col-md-8">
                <table style={{ width: '100%' }}>
                  <tbody>
                    <tr>
                      <td>
                        <Select
                          style={{
                            control: (styles) => ({
                              ...styles,
                              width: 120,
                            }),
                          }}
                          autosize={false}
                          options={hourTimeDefaults}
                          onFocus={onHoursFocusChange(0)}
                          value={formatHoursTime(0, 'open')}
                          onChange={handleHoursChange(0, 'open')}
                        />
                      </td>
                      <td style={{ textAlign: 'center', width: 30 }}>-</td>
                      <td>
                        <Select
                          style={{
                            control: (styles) => ({
                              ...styles,
                              width: 120,
                            }),
                          }}
                          autosize={false}
                          options={hourTimeDefaults}
                          onFocus={onHoursFocusChange(0)}
                          value={formatHoursTime(0, 'close')}
                          onChange={handleHoursChange(0, 'close')}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row" style={{ marginBottom: 6 }}>
              <div className="col-md-4">
                <FormControlLabel
                  id="monday-cb"
                  name="days"
                  control={<Checkbox checked={state.selectedMonday} />}
                  onChange={handleCheckboxChange('selectedMonday')}
                  label="Monday"
                />
              </div>

              <div className="col-md-8">
                <table style={{ width: '100%' }}>
                  <tbody>
                    <tr>
                      <td>
                        <Select
                          style={{
                            control: (styles) => ({
                              ...styles,
                              width: 120,
                            }),
                          }}
                          autosize={false}
                          options={hourTimeDefaults}
                          onFocus={onHoursFocusChange(1)}
                          value={formatHoursTime(1, 'open')}
                          onChange={handleHoursChange(1, 'open')}
                        />
                      </td>
                      <td style={{ textAlign: 'center', width: 30 }}>-</td>
                      <td>
                        <Select
                          style={{
                            control: (styles) => ({
                              ...styles,
                              width: 120,
                            }),
                          }}
                          autosize={false}
                          options={hourTimeDefaults}
                          onFocus={onHoursFocusChange(1)}
                          value={formatHoursTime(1, 'close')}
                          onChange={handleHoursChange(1, 'close')}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row" style={{ marginBottom: 6 }}>
              <div className="col-md-4">
                <FormControlLabel
                  id="tuesday-cb"
                  name="days"
                  control={<Checkbox checked={state.selectedTuesday} />}
                  onChange={handleCheckboxChange('selectedTuesday')}
                  label="Tuesday"
                />
              </div>

              <div className="col-md-8">
                <table style={{ width: '100%' }}>
                  <tbody>
                    <tr>
                      <td>
                        <Select
                          style={{
                            control: (styles) => ({
                              ...styles,
                              width: 120,
                            }),
                          }}
                          autosize={false}
                          options={hourTimeDefaults}
                          onFocus={onHoursFocusChange(2)}
                          value={formatHoursTime(2, 'open')}
                          onChange={handleHoursChange(2, 'open')}
                        />
                      </td>
                      <td style={{ textAlign: 'center', width: 30 }}>-</td>
                      <td>
                        <Select
                          style={{
                            control: (styles) => ({
                              ...styles,
                              width: 120,
                            }),
                          }}
                          autosize={false}
                          options={hourTimeDefaults}
                          onFocus={onHoursFocusChange(2)}
                          value={formatHoursTime(2, 'close')}
                          onChange={handleHoursChange(2, 'close')}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row" style={{ marginBottom: 6 }}>
              <div className="col-md-4">
                <FormControlLabel
                  id="wednesday-cb"
                  name="days"
                  control={<Checkbox checked={state.selectedWednesday} />}
                  onChange={handleCheckboxChange('selectedWednesday')}
                  label="Wednesday"
                />
              </div>

              <div className="col-md-8">
                <table style={{ width: '100%' }}>
                  <tbody>
                    <tr>
                      <td>
                        <Select
                          style={{
                            control: (styles) => ({
                              ...styles,
                              width: 120,
                            }),
                          }}
                          autosize={false}
                          options={hourTimeDefaults}
                          onFocus={onHoursFocusChange(3)}
                          value={formatHoursTime(3, 'open')}
                          onChange={handleHoursChange(3, 'open')}
                        />
                      </td>
                      <td style={{ textAlign: 'center', width: 30 }}>-</td>
                      <td>
                        <Select
                          style={{
                            control: (styles) => ({
                              ...styles,
                              width: 120,
                            }),
                          }}
                          autosize={false}
                          options={hourTimeDefaults}
                          onFocus={onHoursFocusChange(3)}
                          value={formatHoursTime(3, 'close')}
                          onChange={handleHoursChange(3, 'close')}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row" style={{ marginBottom: 6 }}>
              <div className="col-md-4">
                <FormControlLabel
                  id="thursday-cb"
                  name="days"
                  control={<Checkbox checked={state.selectedThursday} />}
                  onChange={handleCheckboxChange('selectedThursday')}
                  label="Thursday"
                />
              </div>

              <div className="col-md-8">
                <table style={{ width: '100%' }}>
                  <tbody>
                    <tr>
                      <td>
                        <Select
                          style={{
                            control: (styles) => ({
                              ...styles,
                              width: 120,
                            }),
                          }}
                          autosize={false}
                          options={hourTimeDefaults}
                          onFocus={onHoursFocusChange(4)}
                          value={formatHoursTime(4, 'open')}
                          onChange={handleHoursChange(4, 'open')}
                        />
                      </td>
                      <td style={{ textAlign: 'center', width: 30 }}>-</td>
                      <td>
                        <Select
                          style={{
                            control: (styles) => ({
                              ...styles,
                              width: 120,
                            }),
                          }}
                          autosize={false}
                          options={hourTimeDefaults}
                          onFocus={onHoursFocusChange(4)}
                          value={formatHoursTime(4, 'close')}
                          onChange={handleHoursChange(4, 'close')}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row" style={{ marginBottom: 6 }}>
              <div className="col-md-4">
                <FormControlLabel
                  id="friday-cb"
                  name="days"
                  control={<Checkbox checked={state.selectedFriday} />}
                  onChange={handleCheckboxChange('selectedFriday')}
                  label="Friday"
                />
              </div>

              <div className="col-md-8">
                <table style={{ width: '100%' }}>
                  <tbody>
                    <tr>
                      <td>
                        <Select
                          style={{
                            control: (styles) => ({
                              ...styles,
                              width: 120,
                            }),
                          }}
                          autosize={false}
                          options={hourTimeDefaults}
                          onFocus={onHoursFocusChange(5)}
                          value={formatHoursTime(5, 'open')}
                          onChange={handleHoursChange(5, 'open')}
                        />
                      </td>
                      <td style={{ textAlign: 'center', width: 30 }}>-</td>
                      <td>
                        <Select
                          style={{
                            control: (styles) => ({
                              ...styles,
                              width: 120,
                            }),
                          }}
                          autosize={false}
                          options={hourTimeDefaults}
                          onFocus={onHoursFocusChange(5)}
                          value={formatHoursTime(5, 'close')}
                          onChange={handleHoursChange(5, 'close')}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row" style={{ marginBottom: 6 }}>
              <div className="col-md-4">
                <FormControlLabel
                  id="saturday-cb"
                  name="days"
                  control={<Checkbox checked={state.selectedSaturday} />}
                  onChange={handleCheckboxChange('selectedSaturday')}
                  label="Saturday"
                />
              </div>

              <div className="col-md-8">
                <table style={{ width: '100%' }}>
                  <tbody>
                    <tr>
                      <td>
                        <Select
                          style={{
                            control: (styles) => ({
                              ...styles,
                              width: 120,
                            }),
                          }}
                          autosize={false}
                          options={hourTimeDefaults}
                          onFocus={onHoursFocusChange(6)}
                          value={formatHoursTime(6, 'open')}
                          onChange={handleHoursChange(6, 'open')}
                        />
                      </td>
                      <td style={{ textAlign: 'center', width: 30 }}>-</td>
                      <td>
                        <Select
                          style={{
                            control: (styles) => ({
                              ...styles,
                              width: 120,
                            }),
                          }}
                          autosize={false}
                          options={hourTimeDefaults}
                          onFocus={onHoursFocusChange(6)}
                          value={formatHoursTime(6, 'close')}
                          onChange={handleHoursChange(6, 'close')}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </div>
      </div>
      <div className="modal-footer">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setShowHoursModal(false)}
        >
          {'Cancel'}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            // TODO Save logic
          }}
        >
          {'Set'}
        </button>
      </div>
    </Modal>
  );

  return (
    <>
      <Breadcrumb
        title={state.selectedName || 'Add New Venue'}
        label="Editor"
        parent="Venues"
        badge={getBadgeTitle()}
        badgeColor={getBadgeColor()}
        onBadgeClick={() => {
          alert('Coming soon!');
          // TODO This is going to allow them to toggle the active state
        }}
      />

      <div style={{ display: 'flex' }}>
        <div style={{ flex: 1 }} />
        <button
          className="btn btn-primary"
          onClick={saveVenue}
          style={isMobile ? { padding: '6px 14px' } : {}}
        >
          {isMobile ? <i className="icon icon-plus" /> : 'Save'}
        </button>
      </div>

      <div
        className="venue-editor row"
        style={{ maxWidth: 1200, margin: '24px auto' }}
      >
        <div className="col-md-8">
          <Card style={{ padding: 10 }}>
            <div>
              <span style={{ fontWeight: 700 }}>Name Search</span>
              <div
                style={{
                  marginTop: 10,
                  marginBottom: 10,
                }}
              >
                <Autocomplete
                  apiKey={config.google.api_key}
                  style={{ width: '100%' }}
                  className="form-control"
                  placeholder="Start typing..."
                  value={state.selectedName}
                  onChange={handleInputChange('selectedName')}
                  onPlaceSelected={(place) => {
                    console.log(place);
                    const lat = place.geometry.location.lat();
                    const lng = place.geometry.location.lng();
                    const comp = place.address_components;
                    const address = addressFromComponents(comp);
                    address.latitude = lat;
                    address.longitude = lng;
                    address.formatted_address = place.formatted_address;
                    setState((prev) => ({
                      ...prev,
                      selectedName: place.name,
                      selectedAddress: address,
                      selectedGoogleId: place.place_id,
                    }));
                  }}
                  options={{
                    types: ['establishment'],
                    fields: [
                      'name',
                      'address_components',
                      'geometry.location',
                      'place_id',
                      'formatted_address',
                      'photos',
                      'url',
                      'website',
                      'opening_hours',
                      'international_phone_number',
                    ],
                  }}
                />
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, flex: 1 }}>Description</span>
                <Button
                  disabled={generating === 'description'}
                  onClick={generateDescription}
                >
                  <AutoAwesomeIcon style={{ fontSize: 16 }} />{' '}
                  {generating === 'description' ? 'Generating...' : 'Generate'}
                </Button>
              </div>
              <div
                style={{
                  marginTop: 10,
                  marginBottom: 10,
                }}
              >
                <textarea
                  className="form-control"
                  value={state.selectedDescription}
                  onChange={handleInputChange('selectedDescription')}
                  style={{ minHeight: 150 }}
                />
              </div>
            </div>
          </Card>

          <Card
            style={{
              padding: 10,
              marginTop: 12,
            }}
          >
            <span style={{ fontWeight: 700 }}>Address Search</span>
            <Autocomplete
              apiKey={config.google.api_key}
              style={{ width: '100%' }}
              className="form-control"
              onPlaceSelected={(place) => {
                console.log(place);
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                const comp = place.address_components;
                const address = addressFromComponents(comp);
                address.latitude = lat;
                address.longitude = lng;
                setState((prev) => ({
                  ...prev,
                  selectedAddress: address,
                }));
              }}
              options={{
                types: ['address'],
                fields: [
                  'address_components',
                  'geometry.location',
                  'place_id',
                  'formatted_address',
                  'photos',
                ],
              }}
            />
            {addressComponents()}
          </Card>

          <Card style={{ marginTop: 12, padding: 10 }}>
            <div className="row" style={{ display: 'flex' }}>
              <div className="col-md-12">
                <div>
                  <span style={{ fontWeight: 700 }}>Images</span>
                  <span
                    style={{
                      color: 'rgb(211, 169, 102)',
                      cursor: 'pointer',
                      float: 'right',
                    }}
                    onClick={() => setShowImageUrlModal(true)}
                  >
                    {'Add image from URL'}
                  </span>
                </div>
                <div className="row" style={{ padding: 20 }}>
                  {state.selectedVenueImages.map((image) => (
                    <div
                      key={image.id}
                      className="col-md-3"
                      style={{
                        marginTop: 10,
                        marginBottom: 10,
                        cursor: 'pointer',
                      }}
                    >
                      <div className="image-overlay">
                        <Icon
                          name="trash"
                          className="delete-image-icon"
                          onClick={() => {
                            console.log('Clicked delete icon', image);
                            ModalDialog.show({
                              title: 'Delete image?',
                              message:
                                'Are you sure you want to delete this image?',
                              type: 'error',
                              buttons: [
                                {
                                  label: 'Cancel',
                                  onClick: () => ModalDialog.close(),
                                },
                                {
                                  label: 'Delete',
                                  style: { color: 'rgb(239, 81, 100)' },
                                  onClick: () => {
                                    const images = [
                                      ...state.selectedVenueImages,
                                    ];
                                    const imagesToRemove = [
                                      ...state.selectedImagesToRemove,
                                    ];
                                    const index = images.findIndex(
                                      (img) => img.id === image.id
                                    );
                                    if (index > -1) {
                                      images.splice(index, 1);
                                      imagesToRemove.push(image);
                                      setState((prev) => ({
                                        ...prev,
                                        selectedVenueImages: images,
                                        selectedImagesToRemove: imagesToRemove,
                                      }));
                                    }
                                    ModalDialog.close();
                                  },
                                },
                              ],
                            });
                          }}
                        />
                        <Icon
                          name="maximize2"
                          className="expand-icon"
                          onClick={() => alert('Coming soon!')}
                        />
                      </div>
                      <img
                        src={venueImageUrl(image)}
                        alt="venue"
                        style={{
                          width: '100%',
                          borderRadius: 5,
                          border: '1px solid #dfdfdf',
                        }}
                      />
                    </div>
                  ))}
                  <Dropzone
                    id="venueImages"
                    ref={venueImages}
                    files={state.selectedVenueImages}
                    dropText="Add images or drop here to upload"
                    style={{ padding: 0 }}
                    onDrop={onFileDrop('venueImages')}
                  />
                </div>
              </div>
            </div>
          </Card>

          <Card style={{ marginTop: 12, padding: 10 }}>
            <span style={{ fontWeight: 700 }}>Contact Info</span>
            <div className="row">
              <div className="col-md-4">
                Website
                <div style={{ marginTop: 10, marginBottom: 10 }}>
                  <input
                    className="form-control"
                    type="text"
                    name="website"
                    value={state.selectedWebsite}
                    onChange={handleInputChange('selectedWebsite')}
                  />
                </div>
              </div>
              <div className="col-md-4">
                Phone
                <div style={{ marginTop: 10, marginBottom: 10 }}>
                  <input
                    className="form-control"
                    type="phone"
                    name="phone"
                    value={state.selectedPhone}
                    onChange={handleInputChange('selectedPhone')}
                  />
                </div>
              </div>
              <div className="col-md-4">
                Email
                <div style={{ marginTop: 10, marginBottom: 10 }}>
                  <input
                    className="form-control"
                    type="email"
                    name="email"
                    value={state.selectedEmail}
                    onChange={handleInputChange('selectedEmail')}
                  />
                </div>
              </div>
            </div>
          </Card>

          <Card style={{ padding: 10, marginTop: 12 }}>
            <span style={{ fontWeight: 700 }}>Social</span>
            <div className="row">
              <div className="col-md-4">
                Yelp ID
                <input
                  className="form-control"
                  type="text"
                  name="yelpId"
                  value={state.selectedYelpId}
                  onChange={handleInputChange('selectedYelpId')}
                />
              </div>
              <div className="col-md-4">
                Foursquare ID
                <input
                  className="form-control"
                  type="text"
                  name="foursquareId"
                  value={state.selectedFoursquareId}
                  onChange={handleInputChange('selectedFoursquareId')}
                />
              </div>
              <div className="col-md-4">
                Google Place ID
                <input
                  className="form-control"
                  type="text"
                  name="googleId"
                  value={state.selectedGoogleId}
                  onChange={handleInputChange('selectedGoogleId')}
                />
              </div>
            </div>
            <div className="row" style={{ marginTop: 10 }}>
              <div className="col-md-4">
                X (Twitter)
                <InputGroup>
                  <InputGroupText>@</InputGroupText>
                  <Input
                    className="form-control"
                    type="text"
                    name="twitterId"
                    value={state.selectedTwitterId}
                    placeholder="<username>"
                    onChange={handleInputChange('selectedTwitterId')}
                  />
                </InputGroup>
              </div>
              <div className="col-md-4">
                Facebook
                <InputGroup>
                  <InputGroupText>fb.com/</InputGroupText>
                  <Input
                    className="form-control"
                    type="text"
                    name="facebookId"
                    value={state.selectedFacebookId}
                    placeholder="<username>"
                    onChange={handleInputChange('selectedFacebookId')}
                  />
                </InputGroup>
              </div>
              <div className="col-md-4">
                Instagram
                <InputGroup>
                  <InputGroupText>@</InputGroupText>
                  <Input
                    className="form-control"
                    type="text"
                    name="instagramId"
                    value={state.selectedInstagramId}
                    placeholder="<username>"
                    onChange={handleInputChange('selectedInstagramId')}
                  />
                </InputGroup>
              </div>
            </div>
          </Card>

          <Card style={{ padding: 10, marginTop: 12 }}>
            <div className="row" style={{ display: 'flex' }}>
              <div className="col-md-12">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontWeight: 700, flex: 1 }}>
                    Checkin Summary
                  </span>
                  <Button
                    disabled={generating === 'checkin_summary'}
                    onClick={generateCheckinSummary}
                  >
                    <AutoAwesomeIcon style={{ fontSize: 16 }} />{' '}
                    {generating === 'checkin_summary'
                      ? 'Generating...'
                      : 'Generate'}
                  </Button>
                </div>
                <textarea
                  value={state.selectedCheckinSummary}
                  onChange={(e) =>
                    updateState({ selectedCheckinSummary: e.target.value })
                  }
                  className="form-control"
                  rows={5}
                  style={{
                    marginTop: 10,
                    marginBottom: 10,
                  }}
                />
                <span
                  style={{
                    color:
                      state.selectedCheckinSummary?.length > 600
                        ? '#e04055'
                        : '',
                  }}
                >
                  {state.selectedCheckinSummary?.length || 0} / 600
                </span>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontWeight: 700, flex: 1 }}>FAQs</span>
                    <Button
                      disabled={generating === 'faqs'}
                      onClick={generateFaqs}
                    >
                      <AutoAwesomeIcon style={{ fontSize: 16 }} />{' '}
                      {generating === 'faqs' ? 'Generating...' : 'Generate'}
                    </Button>
                  </div>
                  <div
                    style={{
                      marginTop: 10,
                      marginBottom: 10,
                    }}
                  >
                    <UncontrolledAccordion
                      defaultOpen="0"
                      style={{ marginTop: 10 }}
                    >
                      {state.selectedFaqs?.map((faq, index) => (
                        <AccordionItem>
                          <AccordionHeader targetId={`${index}`}>
                            {faq.question}
                          </AccordionHeader>
                          <AccordionBody accordionId={`${index}`}>
                            Question:
                            <input
                              value={faq.question}
                              className="form-control"
                              style={{
                                marginTop: 10,
                                marginBottom: 10,
                              }}
                            />
                            Answer:
                            <input
                              value={faq.answer}
                              className="form-control"
                              style={{
                                marginTop: 10,
                                marginBottom: 10,
                              }}
                            />
                          </AccordionBody>
                        </AccordionItem>
                      ))}
                    </UncontrolledAccordion>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <Card style={{ padding: 10, marginTop: 12 }}>
            <div className="row" style={{ display: 'flex' }}>
              <div className="col-md-12">
                <div>
                  <span style={{ fontWeight: 700 }}>Virtual Tour</span>
                  {!state.virtualTourEmbedIframe && (
                    <div>
                      Include iframe embed code from Google Maps, Matterport,
                      etc.
                    </div>
                  )}
                  {!state.virtualTourEmbedIframe && (
                    <textarea
                      className="form-control"
                      placeholder="Paste iframe embed code here"
                      value={state.virtualTourEmbedCode}
                      onChange={handleInputChange('virtualTourEmbedCode')}
                    />
                  )}
                  {state.virtualTourEmbedIframe && (
                    <div
                      style={{ width: '100%' }}
                      dangerouslySetInnerHTML={{
                        __html: state.virtualTourEmbedIframe,
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>
        <div className="col-md-4">
          <Card className="venue-dropzone-wrapper" style={{ padding: 10 }}>
            <div>
              <div style={{ width: '100%' }}>
                <Dropzone
                  hideIcon
                  id="venueCoverImage"
                  ref={venueCoverImage}
                  files={
                    state.venueCoverImage
                      ? [
                          {
                            preview: state.venueCoverImage,
                            name: state.venueCoverImage.substring(
                              state.venueCoverImage.lastIndexOf('/') + 1
                            ),
                            size: 0,
                          },
                        ]
                      : undefined
                  }
                  dropText="Drop cover image here"
                  style={{
                    height: 150,
                    width: '100%',
                    padding: 0,
                    overflow: 'hidden',
                  }}
                  imagePreviewStyle={{
                    width: '100%',
                    objectFit: 'contain',
                  }}
                  onDrop={onFileDrop('venueCoverImage')}
                />
              </div>
            </div>
            <div>
              <div style={{ width: 150, margin: '10px auto' }}>
                <Dropzone
                  hideIcon
                  id="venueLogo"
                  ref={venueProfileImage}
                  files={
                    state.venueProfileImage
                      ? [
                          {
                            preview: state.venueProfileImage,
                            name: state.venueProfileImage.substring(
                              state.venueProfileImage.lastIndexOf('/') + 1
                            ),
                            size: 0,
                          },
                        ]
                      : undefined
                  }
                  dropText="Drop logo here"
                  style={{
                    height: 150,
                    width: 150,
                    padding: 0,
                    borderRadius: '50%',
                    overflow: 'hidden',
                  }}
                  imagePreviewStyle={{
                    height: 146,
                    width: 146,
                    objectFit: 'contain',
                  }}
                  onDrop={onFileDrop('venueProfileImage')}
                />
              </div>
            </div>
          </Card>
          <Card style={{ padding: 10, marginTop: 12 }}>
            <div className="row">
              <div className="col-md-12">
                <div style={{ fontWeight: 700, marginBottom: 12 }}>
                  Tags / Amenities
                </div>
                <AsyncCreatableSelect
                  value={state.selectedTags}
                  onChange={handleSelectChange('selectedTags')}
                  defaultOptions={defaultAmenities.map((value) => ({
                    value,
                    label: value,
                  }))}
                  isMulti
                  isOptionSelected={(option) => state.selectedTags === option}
                />
              </div>
            </div>
            <div style={{ margin: 3 }}>
              <div>
                <FormControlLabel
                  control={<Checkbox checked={state.selectedCanBuy} />}
                  onChange={handleCheckboxChange('selectedCanBuy')}
                  label="Can Buy In Shop"
                />
              </div>
              <div>
                <FormControlLabel
                  control={<Checkbox checked={state.selectedCanBuyOnline} />}
                  onChange={handleCheckboxChange('selectedCanBuyOnline')}
                  label="Can Buy Online"
                />
              </div>
              <div>
                <FormControlLabel
                  control={<Checkbox checked={state.selectedCanSmoke} />}
                  onChange={handleCheckboxChange('selectedCanSmoke')}
                  label="Can Smoke"
                />
              </div>
            </div>
          </Card>
          {/* <Card style={{ padding: 10, marginTop: 12 }}> */}
          {/*  <div> */}
          {/*    <span style={{ fontWeight: 700 }}>Owner Info</span> */}
          {/*    <span */}
          {/*      style={{ */}
          {/*        color: 'rgb(211, 169, 102)', */}
          {/*        cursor: 'pointer', */}
          {/*        float: 'right', */}
          {/*      }} */}
          {/*    > */}
          {/*      {'Edit'} */}
          {/*    </span> */}
          {/*  </div> */}
          {/*  Coming soon */}
          {/* </Card> */}
          <Card style={{ padding: 10, marginTop: 12 }}>
            <div>
              <span style={{ fontWeight: 700 }}>Business Hours</span>
              <span
                style={{
                  color: 'rgb(211, 169, 102)',
                  cursor: 'pointer',
                  float: 'right',
                }}
                onClick={() => setShowHoursModal(true)}
              >
                {'Manage'}
              </span>
            </div>
            {!state.selectedHours && 'No hours added'}
            {state.selectedHours && renderBusinessHours()}
          </Card>
          <Card style={{ padding: 10, marginTop: 12 }}>
            <div>
              <span style={{ fontWeight: 700 }}>Upcoming Events</span>
              <span
                style={{
                  color: 'rgb(211, 169, 102)',
                  cursor: 'pointer',
                  float: 'right',
                }}
                onClick={() => {
                  if (params?.id && params.id !== 'new') {
                    navigate(
                      `/events/manager?action=add&venue_id=${params?.id}`
                    );
                  } else {
                    // TODO Should the button be hidden? Save the changes to the
                    //  venue first, then redirect to events manager? Similar to
                    //  vitola logic on CigarEditor
                  }
                }}
              >
                {'Manage'}
              </span>
            </div>
            {!state.selectedEvents && 'No events added'}
            {state.selectedEvents && renderEvents()}
          </Card>
        </div>
      </div>
      <div style={{ display: 'flex' }}>
        <div style={{ flex: 1 }} />
        {hasId && (
          <>
            <button
              id="merge"
              style={{ marginRight: 10 }}
              type="button"
              className="btn btn-light btn-sm"
              onClick={() => setShowMergeModal(true)}
            >
              {'Merge'}
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => {
                ModalDialog.show({
                  type: 'warning',
                  title: 'Delete Venue?',
                  message:
                    'This will delete the venue and all details associated with it. Note: If users checkins, this will return an error. If that happens, you will need to merge the venue instead. Alternatively, simply set the venue\'s status to "Inactive" or set the business hours to "Closed Permanently".',
                  buttons: [
                    {
                      label: 'No, Cancel',
                      onClick: () => {
                        ModalDialog.close();
                      },
                    },
                    {
                      label: 'Yes, Delete',
                      color: 'danger',
                      onClick: () => deleteVenue(),
                    },
                  ],
                });
              }}
              style={
                isMobile
                  ? { padding: '6px 14px', marginRight: 10 }
                  : { marginRight: 10 }
              }
            >
              {isMobile ? <i className="icon icon-trash" /> : 'Delete'}
            </button>
          </>
        )}
        <button
          className="btn btn-primary btn-sm"
          onClick={saveVenue}
          style={isMobile ? { padding: '6px 14px' } : {}}
        >
          {isMobile ? <i className="icon icon-plus" /> : 'Save'}
        </button>
      </div>
      {renderImageFromUrlModal()}
      {renderMediaCropper()}
      {renderHoursModal()}
    </>
  );
};

export default VenueEditor;
